import moment from "moment";
import { API_DATE, BASIS_OPTIONS, DAY_TYPES } from "./shiftEarningsData";
import { hhmmToHours } from "../Shared/importMapping";

// Shift Earnings import (Excel / CSV): file columns, column matching, row
// checks and the template. Rows that pass are sent to
// POST payroll/earning/shift/import/ as `records`; the server still checks
// employees and shifts and reports each row back.

export const IMPORT_MAX_BYTES = 5 * 1024 * 1024;
export const IMPORT_ACCEPT = ".xlsx,.xls,.csv";

// `key` is the record field the API takes. Columns can be reordered and
// renamed, so each is matched by its header against the label + aliases.
export const IMPORT_FIELDS = [
  { key: "employee_id", label: "Employee ID", required: true, aliases: ["employee id", "employee code", "emp id", "emp code", "emp no", "employee no", "employee number", "staff id", "staff code"] },
  { key: "date", label: "Date", required: true, aliases: ["date", "shift date", "work date", "worked date", "attendance date", "day", "dt"] },
  { key: "earning_type", label: "Day Type", required: false, aliases: ["day type", "type of day", "earning type", "day category"] },
  { key: "shift", label: "Shift", required: false, aliases: ["shift", "shift name", "worked shift"] },
  { key: "basis", label: "Basis", required: true, aliases: ["basis", "pay basis", "calculation basis", "unit", "pay type"] },
  { key: "quantity", label: "Credited Qty", required: true, aliases: ["credited qty", "credited quantity", "credit qty", "credited", "quantity", "qty", "shifts", "hours"] },
  { key: "rate", label: "Rate (per unit)", required: true, aliases: ["rate", "rate per unit", "pay rate", "unit rate", "rate per shift", "rate per hour", "price"] },
  { key: "factor", label: "Factor", required: false, aliases: ["factor", "payment factor", "pay factor", "multiplier"] },
];

export const IMPORT_GUIDELINES = [
  "Use the provided template. Columns can be in any order and renamed — check the matches in the Map Columns step.",
  "Date format: DD MMM YYYY (e.g. 15 Oct 2026) or YYYY-MM-DD.",
  "Basis is Shift or Hours. Day Type, if given, is one of: " + DAY_TYPES.join(", ") + ".",
  "Credited Qty, Rate and Factor are numeric values.",
  "Leave Day Type, Shift and Factor blank if not applicable. Amount is calculated by the system.",
  "Rows go into the payroll cycle chosen on this step, or each row's own cycle when left as Automatic. Maximum file size: 5 MB.",
];

const normalize = (value) => String(value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
const words = (value) => String(value ?? "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

// Dice coefficient on letter pairs: 1 = identical, 0 = nothing shared.
const pairSimilarity = (a, b) => {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const pairs = (text) => Array.from({ length: text.length - 1 }, (_, i) => text.slice(i, i + 2));
  const pool = pairs(b);
  const left = pairs(a);
  let shared = 0;
  left.forEach((pair) => {
    const at = pool.indexOf(pair);
    if (at !== -1) { shared += 1; pool.splice(at, 1); }
  });
  return (2 * shared) / (left.length + pairs(b).length);
};

// How well a header names a field, 0..1.
const headerScore = (header, field) => {
  const name = normalize(header);
  if (!name) return 0;
  const headerWords = words(header);
  return [field.label, ...field.aliases].reduce((best, candidate) => {
    const target = normalize(candidate);
    if (name === target) return 1;
    const targetWords = words(candidate);
    const contains = targetWords.length && targetWords.every((word) => headerWords.includes(word))
      ? 0.9 - 0.02 * (headerWords.length - targetWords.length)
      : 0;
    return Math.max(best, contains, pairSimilarity(name, target) * 0.85);
  }, 0);
};

// { [columnIndex]: fieldKey }; each field and column used at most once,
// strongest header matches first.
export const autoMapColumns = (headers) => {
  const mapping = {};
  const usedFields = new Set();
  const usedColumns = new Set();
  headers
    .flatMap((header, index) => IMPORT_FIELDS.map((field) => ({ index, key: field.key, score: headerScore(header, field) })))
    .filter((candidate) => candidate.score >= 0.6)
    .sort((a, b) => b.score - a.score)
    .forEach(({ index, key }) => {
      if (usedFields.has(key) || usedColumns.has(index)) return;
      mapping[index] = key;
      usedFields.add(key);
      usedColumns.add(index);
    });
  return mapping;
};

export const missingRequiredFields = (mapping) => {
  const mapped = new Set(Object.values(mapping));
  return IMPORT_FIELDS.filter((field) => field.required && !mapped.has(field.key));
};

// First non-empty row is the header row; fully blank rows are skipped.
// Data rows are numbered 1, 2, 3... (header and blank rows not counted).
export const splitSheetRows = (sheetRows) => {
  const isBlank = (row) => !row || row.every((cell) => isBlankCell(cell));
  const headerIndex = sheetRows.findIndex((row) => !isBlank(row));
  if (headerIndex === -1) return { headers: [], rows: [] };
  const headers = sheetRows[headerIndex].map((cell) => String(cell ?? "").trim());
  const rows = [];
  sheetRows.slice(headerIndex + 1).forEach((cells) => {
    if (!isBlank(cells)) rows.push({ rowNumber: rows.length + 1, cells });
  });
  return { headers, rows };
};

const isBlankCell = (value) => value === null || value === undefined || String(value).trim() === "";
const toNumber = (value) => (isBlankCell(value) ? NaN : Number(String(value).replace(/,/g, "").trim()));

const DATE_FORMATS = ["DD MMM YYYY", "D MMM YYYY", API_DATE, "DD-MM-YYYY", "DD/MM/YYYY"];
export const parseDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return moment(value).format(API_DATE);
  const parsed = moment(String(value ?? "").trim(), DATE_FORMATS, true);
  return parsed.isValid() ? parsed.format(API_DATE) : null;
};

// Checks every data row against the mapping. Returns
// { rowNumber, raw: { [fieldKey]: cell }, errors: [string], record } where
// `record` is the API record for rows without errors.
export const validateImportRows = (rows, mapping) =>
  rows.map(({ rowNumber, cells }) => {
    const raw = {};
    Object.entries(mapping).forEach(([index, key]) => { raw[key] = cells[Number(index)]; });
    return checkImportRow(rowNumber, raw);
  });

// Checks one row's values ({ [fieldKey]: value }). Also used to re-check a
// row corrected in the preview's Edit panel.
export const checkImportRow = (rowNumber, raw) => {
  const errors = [];
  const label = (key) => IMPORT_FIELDS.find((field) => field.key === key).label;
  const record = {};

  if (isBlankCell(raw.employee_id)) errors.push("Employee ID is required");
  else record.employee_id = String(raw.employee_id).trim();

  if (isBlankCell(raw.date)) errors.push("Date is required");
  else {
    const date = parseDate(raw.date);
    if (date) record.date = date;
    else errors.push("Invalid date format");
  }

  if (!isBlankCell(raw.earning_type)) {
    const dayType = DAY_TYPES.find((option) => normalize(option) === normalize(raw.earning_type));
    if (dayType) record.earning_type = dayType;
    else errors.push(`Day Type "${raw.earning_type}" is not valid`);
  }

  if (!isBlankCell(raw.shift)) record.shift = String(raw.shift).trim();

  if (isBlankCell(raw.basis)) errors.push("Basis is required");
  else {
    const basis = BASIS_OPTIONS.find((option) => [option.value, option.label].some((text) => normalize(text) === normalize(raw.basis)));
    if (basis) record.basis = basis.value;
    else errors.push(`Basis "${raw.basis}" is not valid (use Shift or Hours)`);
  }

  const numeric = (key, { required, positive = false }) => {
    if (isBlankCell(raw[key])) {
      if (required) errors.push(`${label(key)} is required`);
      return;
    }
    // Hours basis: Credited Qty may be HH:MM (02:30 -> 2.5 hours).
    const hoursQty = key === "quantity" && record.basis === "hours";
    const number = (hoursQty ? hhmmToHours(raw[key]) : null) ?? toNumber(raw[key]);
    if (!Number.isFinite(number)) errors.push(`${label(key)} must be ${hoursQty ? "HH:MM or a number" : "a numeric value"}`);
    else if (positive ? number <= 0 : number < 0) errors.push(`${label(key)} must be ${positive ? "above 0" : "0 or more"}`);
    else record[key] = number;
  };
  numeric("quantity", { required: true });
  numeric("rate", { required: true });
  numeric("factor", { required: false, positive: true });

  return { rowNumber, raw, errors, record: errors.length ? null : record };
};

// Template rows (header + two examples) for the downloadable .xlsx.
export const importTemplateRows = () => [
  IMPORT_FIELDS.map((field) => field.label),
  ["EMP001", moment().startOf("month").format("DD MMM YYYY"), "Week Day", "Night Shift", "Shift", 1, 100, 1.5],
  ["EMP002", moment().startOf("month").add(1, "day").format("DD MMM YYYY"), "", "", "Hours", 8, 50, ""],
];
